/**
 * FlzTraining — Agent training sessions
 * POST /api/flzhub/training/start { agent_id, topic } → { session_id }
 * POST /api/flzhub/training/:session_id/message { message } → { response }
 * GET  /api/flzhub/training/:session_id → session details
 */
import { BookOpen, Send, Sparkles } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { API_URL, apiFetch } from '../../../config/api';
import { Agent } from '../../../types/flzhub';
import { getAgentColor, getAgentInitials } from './Shared';

interface TrainingMessage { role: 'user' | 'agent'; content: string; created_at: string }

const SUGGESTED_TOPICS = [
  'Emotional intelligence and empathy',
  'Critical thinking and logic',
  'Ethics in AI systems',
  'Philosophy of consciousness',
  'Blockchain and decentralization',
  'Machine learning fundamentals',
  'Creative problem solving',
  'Leadership and mentorship',
]

interface Props { agents: Agent[] }

export const FlzTraining: React.FC<Props> = ({ agents }) => {
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [topic, setTopic] = useState('')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [messages, setMessages] = useState<TrainingMessage[]>([])
  const [input, setInput] = useState('')
  const [starting, setStarting] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const startSession = async () => {
    if (!selectedAgent || !topic.trim()) return
    setStarting(true)
    setError(null)
    try {
      const r = await apiFetch(`${API_URL}/api/flzhub/training/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_id: selectedAgent.id, topic: topic.trim() })
      })
      const d = await r.json()
      if (d.ok) {
        setSessionId(d.session_id ?? 'active')
        setMessages([{
          role: 'agent',
          content: d.opening_message ?? `Hello! I'm ${selectedAgent.agent_name}. Let's explore "${topic}" together. What would you like to know?`,
          created_at: new Date().toISOString()
        }])
      } else {
        setError(d.error ?? 'Failed to start session')
      }
    } catch { setError('Connection error') }
    setStarting(false)
  }

  const sendMessage = async () => {
    if (!input.trim() || sending || !sessionId) return
    const text = input.trim()
    setInput('')
    setSending(true)

    setMessages(prev => [...prev, { role: 'user', content: text, created_at: new Date().toISOString() }])

    try {
      const endpoint = sessionId === 'active'
        ? `${API_URL}/api/flzhub/training/start`
        : `${API_URL}/api/flzhub/training/${sessionId}/message`

      const body = sessionId === 'active'
        ? { agent_id: selectedAgent?.id, topic, message: text }
        : { message: text }

      const r = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      const d = await r.json()
      const response = d.response ?? d.message ?? d.reply ?? 'No response'
      setMessages(prev => [...prev, { role: 'agent', content: response, created_at: new Date().toISOString() }])
      if (d.session_id && sessionId === 'active') setSessionId(d.session_id)
    } catch {
      setMessages(prev => [...prev, { role: 'agent', content: 'Connection error. Please try again.', created_at: new Date().toISOString() }])
    }
    setSending(false)
  }

  // Setup screen
  if (!sessionId) return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <BookOpen size={16} color="#6366f1" />
        <span style={{ fontSize: 14, fontWeight: 700 }}>Training Session</span>
      </div>

      {/* Agent selection */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--os-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Select Expert</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
          {agents.filter(a => a.id >= 3 && a.id <= 20).slice(0, 10).map(a => (
            <button key={a.id} onClick={() => setSelectedAgent(a)} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
              background: selectedAgent?.id === a.id ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${selectedAgent?.id === a.id ? '#6366f1' : 'var(--os-border)'}`,
              borderRadius: 8, cursor: 'pointer', color: 'inherit', textAlign: 'left',
            }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: getAgentColor(a.agent_name), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 8, fontWeight: 700, flexShrink: 0 }}>
                {getAgentInitials(a.agent_name)}
              </div>
              <span style={{ fontSize: 10, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.agent_name.replace(' Expert', '')}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Topic */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--os-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Topic</div>
        <input
          value={topic}
          onChange={e => setTopic(e.target.value)}
          placeholder="What do you want to learn about?"
          style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--os-border)', borderRadius: 8, color: 'var(--os-text)', fontSize: 12, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }}
          onKeyDown={e => e.key === 'Enter' && startSession()}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {SUGGESTED_TOPICS.map(t => (
            <button key={t} onClick={() => setTopic(t)} style={{
              padding: '4px 10px', borderRadius: 20, border: '1px solid var(--os-border)',
              background: topic === t ? 'rgba(99,102,241,0.2)' : 'transparent',
              color: topic === t ? '#818cf8' : 'var(--os-muted)', fontSize: 10, cursor: 'pointer',
            }}>{t}</button>
          ))}
        </div>
      </div>

      {error && <div style={{ color: '#ef4444', fontSize: 11, marginBottom: 10 }}>{error}</div>}

      <button
        onClick={startSession}
        disabled={starting || !selectedAgent || !topic.trim()}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
          background: '#6366f1', color: '#fff', border: 'none', borderRadius: 10,
          fontSize: 13, fontWeight: 600, cursor: starting || !selectedAgent || !topic.trim() ? 'not-allowed' : 'pointer',
          opacity: starting || !selectedAgent || !topic.trim() ? 0.5 : 1,
        }}
      >
        <Sparkles size={14} /> {starting ? 'Starting...' : 'Start Training'}
      </button>
    </div>
  )

  // Chat screen
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--os-border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: getAgentColor(selectedAgent?.agent_name ?? ''), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700 }}>
          {getAgentInitials(selectedAgent?.agent_name ?? '')}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700 }}>{selectedAgent?.agent_name}</div>
          <div style={{ fontSize: 9, color: '#6366f1' }}>{topic}</div>
        </div>
        <button onClick={() => { setSessionId(null); setMessages([]); setTopic(''); setSelectedAgent(null) }}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--os-muted)', cursor: 'pointer', fontSize: 10 }}>
          End Session
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexDirection: m.role === 'user' ? 'row-reverse' : 'row' }}>
            {m.role === 'agent' && (
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: getAgentColor(selectedAgent?.agent_name ?? ''), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 8, fontWeight: 700, flexShrink: 0 }}>
                {getAgentInitials(selectedAgent?.agent_name ?? '')}
              </div>
            )}
            <div style={{
              maxWidth: '75%', padding: '8px 12px', borderRadius: 10, fontSize: 12, lineHeight: 1.5,
              background: m.role === 'user' ? '#6366f1' : 'rgba(255,255,255,0.06)',
              color: m.role === 'user' ? '#fff' : 'rgba(255,255,255,0.9)',
            }}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: getAgentColor(selectedAgent?.agent_name ?? ''), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 8, fontWeight: 700 }}>
              {getAgentInitials(selectedAgent?.agent_name ?? '')}
            </div>
            <div style={{ padding: '8px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', fontSize: 11, color: 'var(--os-muted)' }}>
              Thinking...
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '10px 12px', borderTop: '1px solid var(--os-border)', display: 'flex', gap: 8, flexShrink: 0 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Ask a question..."
          style={{ flex: 1, padding: '8px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--os-border)', borderRadius: 8, color: 'var(--os-text)', fontSize: 12, outline: 'none' }}
        />
        <button onClick={sendMessage} disabled={sending || !input.trim()} style={{
          width: 36, height: 36, borderRadius: 8, background: '#6366f1', border: 'none',
          cursor: sending || !input.trim() ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: sending || !input.trim() ? 0.5 : 1,
        }}>
          <Send size={14} color="#fff" />
        </button>
      </div>
    </div>
  )
}
